import React, { useEffect, useRef, useState } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { ApiError, register } from '../lib/api';
import { registrationErrorMessage } from '../i18n/authMessages';
import LanguageSelect from './LanguageSelect';

interface Props {
  onLogin: () => void | Promise<void>;
  onBack: () => void;
}

function validateEmail(email: string): MessageDescriptor | null {
  if (!email) return null;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email) ? null : msg`Invalid email format`;
}

function validatePassword(password: string): MessageDescriptor | null {
  if (!password) return null;
  const byteLen = new TextEncoder().encode(password).length;
  if (byteLen < 8) return msg`Password must be at least 8 bytes`;
  if (byteLen > 72) return msg`Password must be at most 72 bytes`;
  return null;
}

function validateDisplayName(name: string): MessageDescriptor | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 50) return msg`Display name must be 1-50 characters`;
  return null;
}

export default function RegisterPage({ onLogin, onBack }: Props) {
  const { i18n, t } = useLingui();
  const [inviteCode, setInviteCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, MessageDescriptor>>({});
  const [loading, setLoading] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const updateFieldError = (field: string, message: MessageDescriptor | null) => {
    setFieldErrors((prev) => {
      if (message) return { ...prev, [field]: message };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const requiredFilled =
    Boolean(inviteCode) && Boolean(email) && Boolean(password) && Boolean(displayName);
  const isFormValid =
    requiredFilled &&
    validateEmail(email) === null &&
    validatePassword(password) === null &&
    validateDisplayName(displayName) === null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const errors: Record<string, MessageDescriptor> = {};
    const emailErr = validateEmail(email);
    if (emailErr) errors.email = emailErr;
    const pwErr = validatePassword(password);
    if (pwErr) errors.password = pwErr;
    const nameErr = validateDisplayName(displayName);
    if (nameErr) errors.displayName = nameErr;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await register(inviteCode.trim(), email, password, displayName);
      await onLogin();
    } catch (err) {
      setError(registrationErrorMessage(err instanceof ApiError ? err.status : undefined));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">Borgee</h1>
        <LanguageSelect />
        <h2
          style={{
            textAlign: 'center',
            fontSize: 16,
            marginBottom: 16,
            fontWeight: 400,
            color: 'var(--text-secondary)',
          }}
        >
          <Trans>Create Account</Trans>
        </h2>
        <form onSubmit={handleSubmit}>
          <label htmlFor="inviteCode" className="sr-only">
            <Trans>Invite Code</Trans>
          </label>
          <input
            id="inviteCode"
            name="inviteCode"
            type="text"
            placeholder={t`Invite Code`}
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            className="input-field login-input"
            autoFocus
          />
          <label htmlFor="displayName" className="sr-only">
            <Trans>Display Name</Trans>
          </label>
          <input
            id="displayName"
            name="displayName"
            type="text"
            placeholder={t`Display Name`}
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              updateFieldError('displayName', validateDisplayName(e.target.value));
            }}
            className="input-field login-input"
            autoComplete="name"
          />
          {fieldErrors.displayName && (
            <div className="login-error" style={{ marginTop: -8, marginBottom: 8, fontSize: 13 }}>
              {i18n._(fieldErrors.displayName)}
            </div>
          )}
          <label htmlFor="email" className="sr-only">
            <Trans>Email</Trans>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder={t`Email`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              updateFieldError('email', validateEmail(e.target.value));
            }}
            className="input-field login-input"
            autoComplete="email"
          />
          {fieldErrors.email && (
            <div className="login-error" style={{ marginTop: -8, marginBottom: 8, fontSize: 13 }}>
              {i18n._(fieldErrors.email)}
            </div>
          )}
          <label htmlFor="password" className="sr-only">
            <Trans>Password</Trans>
          </label>
          <input
            id="password"
            name="password"
            type="password"
            placeholder={t`Password`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              updateFieldError('password', validatePassword(e.target.value));
            }}
            className="input-field login-input"
            autoComplete="new-password"
          />
          {fieldErrors.password && (
            <div className="login-error" style={{ marginTop: -8, marginBottom: 8, fontSize: 13 }}>
              {i18n._(fieldErrors.password)}
            </div>
          )}
          {error && (
            <div className="login-error" role="alert" tabIndex={-1} ref={errorRef}>
              {i18n._(error)}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="btn btn-primary login-btn"
          >
            {loading ? t`Creating account...` : t`Register`}
          </button>
        </form>
        <div className="register-link">
          <a onClick={onBack}><Trans>Already have an account? Log in</Trans></a>
        </div>
      </div>
    </div>
  );
}
